// Auto straighten (§11.19 "Straighten" cleaner, non-AI): Canny edges + a Hough transform on a ≤ 1024 px
// version; the dominant near-horizontal or near-vertical line within ±10°. Returns that line's end points
// (source pixels of the given image) for geometry.straightenAngle, or null when no line is confident.
export type DetectedLine = { x0: number; y0: number; x1: number; y1: number; angle: number; votes: number };

const MAX_TILT = 10; // degrees
const STEP = 0.1; // degrees per Hough bin

function blur(src: Float32Array, W: number, H: number, sigma: number): Float32Array {
  const r = Math.ceil(sigma * 3);
  const k: number[] = [];
  let ks = 0;
  for (let i = -r; i <= r; i++) {
    const w = Math.exp((-0.5 * i * i) / (sigma * sigma));
    k.push(w);
    ks += w;
  }
  const tmp = new Float32Array(W * H),
    out = new Float32Array(W * H);
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      let a = 0;
      for (let i = -r; i <= r; i++) a += k[i + r]! * src[y * W + Math.min(W - 1, Math.max(0, x + i))]!;
      tmp[y * W + x] = a / ks;
    }
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      let a = 0;
      for (let i = -r; i <= r; i++) a += k[i + r]! * tmp[Math.min(H - 1, Math.max(0, y + i)) * W + x]!;
      out[y * W + x] = a / ks;
    }
  return out;
}

/** Canny edge map (Sobel, non-maximum suppression, hysteresis); also returns the gradient direction. */
export function canny(gray: Float32Array, W: number, H: number) {
  const g = blur(gray, W, H, 1.4);
  const mag = new Float32Array(W * H),
    gx = new Float32Array(W * H),
    gy = new Float32Array(W * H);
  const at = (x: number, y: number) =>
    g[Math.min(H - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x))]!;
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const sx =
        at(x + 1, y - 1) +
        2 * at(x + 1, y) +
        at(x + 1, y + 1) -
        at(x - 1, y - 1) -
        2 * at(x - 1, y) -
        at(x - 1, y + 1);
      const sy =
        at(x - 1, y + 1) +
        2 * at(x, y + 1) +
        at(x + 1, y + 1) -
        at(x - 1, y - 1) -
        2 * at(x, y - 1) -
        at(x + 1, y - 1);
      gx[y * W + x] = sx;
      gy[y * W + x] = sy;
      mag[y * W + x] = Math.hypot(sx, sy);
    }
  const nms = new Float32Array(W * H);
  for (let y = 1; y < H - 1; y++)
    for (let x = 1; x < W - 1; x++) {
      const i = y * W + x;
      const m = mag[i]!;
      if (m === 0) continue;
      const a = (Math.atan2(gy[i]!, gx[i]!) * 180) / Math.PI;
      const d = ((a % 180) + 180) % 180;
      const [dx, dy] = d < 22.5 || d >= 157.5 ? [1, 0] : d < 67.5 ? [1, 1] : d < 112.5 ? [0, 1] : [-1, 1];
      if (m >= mag[i + dy * W + dx]! && m >= mag[i - dy * W - dx]!) nms[i] = m;
    }
  const vals = [...nms].filter((v) => v > 0).sort((a, b) => a - b);
  // Relative threshold (top 10%), with an absolute floor: an edge needs a real step (about 4% luma), so
  // sensor noise or a flat wall never makes a "line".
  const hi = Math.max(0.15, vals.length ? vals[Math.floor(vals.length * 0.9)]! : Infinity);
  const lo = hi * 0.4;
  const edge = new Uint8Array(W * H);
  const stack: number[] = [];
  for (let i = 0; i < W * H; i++)
    if (nms[i]! >= hi) {
      edge[i] = 1;
      stack.push(i);
    }
  while (stack.length) {
    const i = stack.pop()!;
    const x = i % W,
      y = (i / W) | 0;
    for (let dy = -1; dy <= 1; dy++)
      for (let dx = -1; dx <= 1; dx++) {
        const nx = x + dx,
          ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
        const j = ny * W + nx;
        if (!edge[j] && nms[j]! >= lo) {
          edge[j] = 1;
          stack.push(j);
        }
      }
  }
  return { edge, gx, gy };
}

/**
 * Hough over lines tilted at most ±10° from horizontal (family 'h') or vertical ('v'). Each edge pixel
 * votes only for the family its gradient points across. Confidence: the winning line collects at least
 * 25% of the image dimension it spans, and beats the best line of a clearly different angle by 1.5×.
 */
export function detectTilt(gray: Float32Array, W: number, H: number): DetectedLine | null {
  const { edge, gx, gy } = canny(gray, W, H);
  const nA = Math.round((2 * MAX_TILT) / STEP) + 1;
  let best: DetectedLine | null = null;
  let bestScore = 0;
  for (const fam of ['h', 'v'] as const) {
    const span = fam === 'h' ? W : H;
    const R = Math.ceil(Math.hypot(W, H));
    const acc = new Uint32Array(nA * (2 * R + 1));
    const cs = new Float64Array(nA),
      sn = new Float64Array(nA);
    for (let a = 0; a < nA; a++) {
      const t = ((-MAX_TILT + a * STEP) * Math.PI) / 180;
      cs[a] = Math.cos(t);
      sn[a] = Math.sin(t);
    }
    for (let i = 0; i < W * H; i++) {
      if (!edge[i]) continue;
      const ax = Math.abs(gx[i]!),
        ay = Math.abs(gy[i]!);
      if (fam === 'h' ? ay < ax * 2 : ax < ay * 2) continue; // gradient must cross the line family
      const x = i % W,
        y = (i / W) | 0;
      for (let a = 0; a < nA; a++) {
        // 'h': y·cos − x·sin = ρ ;  'v': x·cos + y·sin = ρ   (α = tilt from the axis)
        const rho = fam === 'h' ? y * cs[a]! - x * sn[a]! : x * cs[a]! + y * sn[a]!;
        acc[a * (2 * R + 1) + Math.round(rho) + R]!++;
      }
    }
    let top = 0,
      topA = 0,
      topR = 0;
    for (let a = 0; a < nA; a++)
      for (let r = 0; r <= 2 * R; r++) {
        const v = acc[a * (2 * R + 1) + r]!;
        if (v > top) {
          top = v;
          topA = a;
          topR = r - R;
        }
      }
    // Best line at an angle at least 1° away (a different, competing direction).
    let second = 0;
    for (let a = 0; a < nA; a++) {
      if (Math.abs(a - topA) * STEP < 1) continue;
      for (let r = 0; r <= 2 * R; r++) second = Math.max(second, acc[a * (2 * R + 1) + r]!);
    }
    if (top < 0.25 * span || top < 1.5 * second) continue;
    const score = top / span;
    if (score <= bestScore) continue;
    bestScore = score;
    const alpha = -MAX_TILT + topA * STEP;
    const t = (alpha * Math.PI) / 180;
    if (fam === 'h') {
      // y = (ρ + x·sin) / cos
      const yAt = (x: number) => (topR + x * Math.sin(t)) / Math.cos(t);
      best = { x0: 0, y0: yAt(0), x1: W, y1: yAt(W), angle: alpha, votes: top };
    } else {
      const xAt = (y: number) => (topR - y * Math.sin(t)) / Math.cos(t);
      best = { x0: xAt(0), y0: 0, x1: xAt(H), y1: H, angle: alpha, votes: top };
    }
  }
  return best;
}
